// HR Dashboard API calls. API only: these return data (or a success flag) and never dispatch;
// the page dispatches to Redux after they resolve.
import { toast } from "react-toastify";
import {
    HrDashboardConfig,
    HrExportFormat,
    HrExportView,
    HrGroupBy,
    HrTermination,
    HrTerminationReason,
    HrView,
} from "models/hr-dashboard";

const API = `${process.env.REACT_APP_API_URL}/hr-dashboard`;

const errorMessage = async (res: Response, fallback: string) => {
    const body = await res.json().catch(() => null);
    return body?.message ?? fallback;
};

const getJson = async <T>(url: string, fallback: string): Promise<T | null> => {
    try {
        const res = await fetch(url, { credentials: "include" });
        if (!res.ok) throw new Error(await errorMessage(res, fallback));
        const body = await res.json();
        return body.data as T;
    } catch (err: any) {
        toast.error(err.message ?? fallback);
        return null;
    }
};

const putJson = async <T>(url: string, payload: object, fallback: string): Promise<T | null> => {
    try {
        const res = await fetch(url, {
            method: "PUT",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(await errorMessage(res, fallback));
        const body = await res.json();
        return (body.data ?? true) as T;
    } catch (err: any) {
        toast.error(err.message ?? fallback);
        return null;
    }
};

// API path for each dashboard tab
const VIEW_PATHS: Record<HrView, (fy: number, groupBy: HrGroupBy) => string> = {
    "headcount": fy => `headcount?fy=${fy}&type=employee`,
    "by-group": (fy, groupBy) => `headcount/by-group?fy=${fy}&groupBy=${groupBy}`,
    "consultants": fy => `headcount?fy=${fy}&type=consultant`,
    "compare": fy => `headcount/compare?fy=${fy}`,
    "movement": fy => `headcount?fy=${fy}&type=all`,
    "turnover": fy => `turnover?fy=${fy}`,
    "retention": (fy, groupBy) => `retention?fy=${fy}&groupBy=${groupBy}`,
};

// Data for one tab (see HrDashboardViews in the reducer for the shape of each)
export const getHrView = (view: HrView, fy: number, groupBy: HrGroupBy) =>
    getJson<unknown>(`${API}/${VIEW_PATHS[view](fy, groupBy)}`, "Error loading the HR Dashboard.");

export const getHrDashboardConfig = () =>
    getJson<HrDashboardConfig>(`${API}/config`, "Error loading HR Dashboard settings.");

export const updateHrDashboardConfig = async (config: Omit<HrDashboardConfig, "updatedAt">): Promise<HrDashboardConfig | null> => {
    const saved = await putJson<HrDashboardConfig>(`${API}/config`, config, "Error saving settings.");
    if (saved) toast.success("Settings saved.");
    return saved;
};

export const getHrTerminationReasons = () =>
    getJson<HrTerminationReason[]>(`${API}/reason-map`, "Error loading termination reasons.");

// regrettable = null removes the mapping. Returns the updated reason list.
export const setHrReasonMapping = (reason: string, regrettable: boolean | null) =>
    putJson<HrTerminationReason[]>(`${API}/reason-map`, { reason, regrettable }, "Error saving the mapping.");

export const getHrTerminations = (fy: number) =>
    getJson<HrTermination[]>(`${API}/terminations?fy=${fy}`, "Error loading terminations.");

// regrettable = null removes the override (the reason mapping applies again)
export const setHrTerminationOverride = async (employeeId: string, regrettable: boolean | null): Promise<boolean> => {
    const ok = await putJson<boolean>(`${API}/terminations/${employeeId}/regrettable`, { regrettable }, "Error saving the override.");
    return ok !== null;
};

export const downloadHrDashboardExport = async (view: HrExportView, fy: number, groupBy: HrGroupBy, format: HrExportFormat): Promise<boolean> => {
    try {
        const res = await fetch(`${API}/export?view=${view}&fy=${fy}&groupBy=${groupBy}&format=${format}`, { credentials: "include" });
        if (!res.ok) throw new Error(await errorMessage(res, "Error downloading the export."));

        const blob = await res.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = `hr-${view}-FY${fy}.${format}`;
        a.click();
        window.URL.revokeObjectURL(blobUrl);
        return true;
    } catch (err: any) {
        toast.error(err.message ?? "Error downloading the export.");
        return false;
    }
};
